<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('create_vat_plan')"
        :edit-mode-text="$t('edit_vat_plan')"
        @reset="selectedVatId = null"
    >
        <template v-slot:main-mode>
            <VatFilter
                v-model="filters"
                :has-active-filters="hasActiveFilters"
                @submit="loadData"
                @reset="resetFilters"
            />
            <v-data-table
                :headers="headers"
                :items="vatPlans"
                :items-per-page-options="useTableOptions(vatPlans.length)"
                :items-length="vatPlans.length"
                density="compact"
                class="elevation-1 table-server"
                must-sort
                fixed-header
            >
                <template v-slot:item.name="{item}">
                    <span class="text-link" @click="editSalesSettings(item.id)">
                        {{ item.name }}
                    </span>
                </template>
                <template v-slot:item.active="{item}">
                    {{ item.active ? $t('active') : $t('inactive') }}
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <VatSettingsForm :vat-id="selectedVatId" @save="save()" @cancel="modeSwitcher.goToTable()" />
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import {useTableOptions} from '~/composables/helpers'
    import {isEqual} from '~/utils/helpers'

    const initFilter = () => ({
        active: true,
        nameFilter: null,
    })
    const isLoading = ref<boolean>(false)
    const vatPlans = ref<any[]>([])
    const selectedVatId = ref<number>()
    const modeSwitcher = ref(null)
    const filters = ref<{[key: string]: boolean | string}>(initFilter())

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        isLoading.value = true
        const rq = clone(filters.value)
        clean(rq)
        try {
            vatPlans.value = (await $api.vatPlans.get(rq)).vatPlans
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const headers = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '150px', sortable: true},
            {title: t('status'), value: 'active', width: '140px'},
        ]
    })

    const editSalesSettings = (id: number): void => {
        selectedVatId.value = id
        modeSwitcher.value.edit()
    }

    const save = async (): Promise<void> => {
        modeSwitcher.value.goToTable()
        await loadData()
    }

    const hasActiveFilters = computed(() => {
        return !isEqual(initFilter(), filters.value)
    })

    const resetFilters = (): void => {
        filters.value = initFilter()
        loadData()
    }
</script>
